<svelte:options customElement={{ tag: "bt-welcome-splash", shadow: "none" }} />

<script lang="ts">

import { onMount } from 'svelte';
import { eventBus } from '$shared/lib/eventBus'
import { createTimeline } from 'animejs';

export let ftmMediaId = '';

const timeBeforeAbortInMilliseconds = 20000;

// Element bindings for Anime.js to glom onto
let textElement = null;
let buttonElement;
let bgElementA = null;
let bgElementB = null;
// Timing-related switches
let abortingSplash = false;
let closingBegan = false;
	// Outside of mount scope so can be bound to button
let beginClosing = () => {};
let handleClickSkip = () => {};
let cleanupTimelinesAndRemoveElements = () => {};
let resolvePlayerReady;
const playerReadyPromise = new Promise(resolve => {
	resolvePlayerReady = resolve;
})

let ctaPlaceholder = "All your leadership questions, answered by the world's biggest thinkers.";
let skipToHomePlaceholder = "Skip to home";

const calculateMovementPeriodInMilliseconds = (distanceInPixels, pixelsPerSecond) => {
	// Given a distance in pixels travelled and a fixed rate, returns
	// the amount of time it should take to traverse that length given that rate of travel
	return(distanceInPixels / pixelsPerSecond) * 1000;
}

const updateUserWelcomeSplashFlag = () => {
  fetch(`/welcome_splash/update`, {
    method: 'PATCH',
    credentials: 'same-origin',
    // @ts-ignore
    headers: {
      'Content-Type': 'application/json',
      // @ts-ignore
      'X-CSRF-Token': document
        .querySelector('meta[name="csrf-token"]')
        .getAttribute('content')
    },
    body: JSON.stringify({
    	show_welcome_screen: false
  	})
  }).then((response) => {
    if (!response.ok) {
    	// Exit animations / close the overlay ASAP
  		abortingSplash = true;
			cleanupTimelinesAndRemoveElements();
      throw new Error(`PlusPlayer HTTP error! Status: ${response?.status}`);
    }
  })
}

onMount(() => {
  updateUserWelcomeSplashFlag();

	let player, ctaTimeline, videoTimeline, leftOrbTimeline, rightOrbTimeline, closeTimeline;

	// Calculations to make sure the animation moves at the same pace all around the edge of the window
	let horizontalMovePeriod = calculateMovementPeriodInMilliseconds(window.innerWidth, 50)
	let verticalMovePeriod = calculateMovementPeriodInMilliseconds(window.innerHeight, 50)
	let animationContainer = document.querySelector('.welcome-splash');
	let ctaContainer = document.querySelector('.welcome-splash__cta');
	let videoContainer = document.querySelector('.welcome-splash__video')
	let videoElement = document.querySelector('.welcome-splash__video bt-plus-player')


  const modalOpenOrClosedObserver = new MutationObserver((mutationList) => {
  	for (const mutation of mutationList) {
  		if (mutation.attributeName == 'open') {
  			abortingSplash = true;
	  		cleanupTimelinesAndRemoveElements();
  		}
  	}
	});

	modalOpenOrClosedObserver.observe(animationContainer, {
		subtree: false,
		attributes: true
	});

	leftOrbTimeline = createTimeline({
		loop: false,
		autoplay: true,
	});
	rightOrbTimeline = createTimeline({
		loop: false,
		autoplay: true,
	});

	createTimeline({
		loop: false,
		autoplay: true
	}).sync(leftOrbTimeline).sync(rightOrbTimeline)

	ctaTimeline = createTimeline({
		loop: false,
		autoplay: true,
		onComplete: async () => {
			if (abortingSplash) return;

			const timeout = new Promise((_, reject) =>
				setTimeout(() => reject(new Error('Player timeout')), timeBeforeAbortInMilliseconds)
			);

			try {
				await Promise.race([playerReadyPromise, timeout]);
				if (!abortingSplash) videoTimeline.play();
			} catch {
				if (!closingBegan) beginClosing();
			}
		}
	});

	closeTimeline = createTimeline({
		loop: false,
		autoplay: false,
		defaults: {
			duration: 500,
		},
		onBegin: () => {
			videoTimeline.pause();
			document.body.classList.add('had-welcome-splash');
			closingBegan = true;
		},
		onComplete: () => {
			cleanupTimelinesAndRemoveElements();
		}
	})

	// Background animation timelines
	leftOrbTimeline.set(bgElementA, {
		opacity: 0,
		translateX: 0,
		translateY: 0
	}, 0)
	leftOrbTimeline.add(bgElementA, {
		opacity: .3,
		duration: 500,
	})
	leftOrbTimeline.add(bgElementA, {
		opacity: .3,
		translateX: "-60vw",
		duration: 1000
	})
	leftOrbTimeline.add(bgElementA, {
		translateY: "-80vh",
		duration: 500,
		ease: "linear"
	})
	leftOrbTimeline.add(bgElementA, {
		translateX: "60vw",
		ease: "linear",
		duration: verticalMovePeriod
	})
	leftOrbTimeline.add(bgElementA, {
		opacity: .4,
		translateY: "80vh",
		ease: "linear",
		duration: horizontalMovePeriod
	})
	rightOrbTimeline.set(bgElementB, {
		opacity: 0,
		translateX: 0,
		translateY: 0
	}, 0)
	rightOrbTimeline.add(bgElementB, {
		opacity: .3,
		translateX: 0,
		translateY: 0,
		duration: 500,
	})
	rightOrbTimeline.add(bgElementB, {
		opacity: .3,
		translateX: "60vw",
		duration: 1000
	})
	rightOrbTimeline.add(bgElementB, {
		translateY: "80vh",
		duration: 500,
		ease: "linear"
	})
	rightOrbTimeline.add(bgElementB, {
		translateX: "-60vw",
		ease: "linear",
		duration: verticalMovePeriod
	})
	rightOrbTimeline.add(bgElementB, {
		translateY: "-80vh",
		ease: "linear",
		duration: horizontalMovePeriod
	})

	// Call to action timeline
	ctaTimeline.set(buttonElement, {
		translateY: 50,
		opacity: 0
	}, 0)
	ctaTimeline.set(textElement, {
		translateY: 50,
		opacity: 0,
	}, 0)
	ctaTimeline.add(buttonElement, {
		translateY: 0,
		opacity: 1,
		delay: 600,
	}, 500)
	ctaTimeline.add(textElement, {
		translateY: 0,
		opacity: 1,
		delay: 400
	}, 500)

	// Closing the splash page timeline
	closeTimeline.set(animationContainer, {
		opacity: 1
	}, 0)
	closeTimeline.set(ctaContainer, {
		opacity: 1
	}, 0)
	closeTimeline.set(videoElement, {
		opacity: 1,
		translateY: "0"
	}, 0)
	closeTimeline.add(videoElement, {
		opacity: 0,
		translateY: "2rem",
		duration: 500,
	})
	closeTimeline.add(ctaContainer, {
		opacity: 0,
		translateY: "2rem",
		duration: 500,
	})
	closeTimeline.add(animationContainer, {
		opacity: 0,
		duration: 500,
	})

	eventBus.on('plus-player-ready', event => {
		player = event.playerElement;
		player.querySelector('media-play-button').focus();

		videoTimeline = createTimeline({ loop: false, autoplay: false });
		videoTimeline.set(videoContainer, { opacity: 0, translateY: 50 }, 0);
		videoTimeline.add(videoContainer, { opacity: 1, translateY: 0 }, 500);

		resolvePlayerReady();

	});

	eventBus.on('plus-player-end', event => {
		if (ftmMediaId === event.ftmMediaId) {
			window.setTimeout(() => {
				if (!closingBegan) {
					closeTimeline.play();
				}
			}, 2000)
		}
	});

	eventBus.on('plus-player-failed', event => {
		if (ftmMediaId === event.ftmMediaId) {
			closeTimeline.set(videoContainer, {
				opacity: 0
			}, 0)
			window.setTimeout(() => {
				if (!closingBegan) {
					closeTimeline.play();
				}
			}, 2000)
		}
	});

	beginClosing = () => {
		closingBegan = true;
		[player, videoTimeline, leftOrbTimeline, rightOrbTimeline, ctaTimeline].forEach(itemToPause => {
			if (itemToPause) { itemToPause.pause(); }
		});
		closeTimeline.play();
	}

	handleClickSkip = () => {
		window.amplitude?.track("Splash Page Skip To Home Clicked");
		if (!closingBegan) {
			beginClosing();
		}
	}

	cleanupTimelinesAndRemoveElements = () => {
		closingBegan = true;
		[videoTimeline, leftOrbTimeline, rightOrbTimeline, ctaTimeline, closeTimeline].forEach(timeline => {
			if (timeline) timeline.cancel();
		})
		if (player) {
			player.pause();
		}
		animationContainer.classList.add('is-leaving');
		window.scrollTo({top: 0, behavior: 'instant'});
		[bgElementA, bgElementB, textElement, buttonElement,
		ctaContainer].forEach(element => {
			if (element) {
				element.remove();
			}
		});
		videoContainer.remove();
		animationContainer.remove();
	}

	return () => {
		if (player) player.pause();
		document.body.classList.remove('had-welcome-splash');
	}
});

</script>

<div class="bg-1" bind:this={bgElementA}></div>
<div class="bg-2" bind:this={bgElementB}></div>
<div class="welcome-splash__cta">
  <p bind:this={textElement}>{ctaPlaceholder}</p>
  <button bind:this={buttonElement} type="button" on:click={handleClickSkip}>{skipToHomePlaceholder}</button>
</div>

<style>
	.welcome-splash__cta {
		position: absolute;
		bottom: 12rem;
		left: 0;
		right: 0;
		margin: auto;
		z-index: 9999999;
		text-align: center;
		@media (max-height: 58rem) {
			bottom: 8rem;
		}
		@media (max-height: 54rem) {
			bottom: 4em;
		}
	}
	p {
		font-family: theme('fontFamily.serif');
		color: white;
		font-size: theme('fontSize.4xl');
		letter-spacing: -0.5px;
		margin-bottom: theme('spacing.6');
		margin-top: theme('spacing.10');
		font-weight: 400;
		-webkit-font-smoothing: antialiased;
		-moz-osx-font-smoothing: grayscale;
		opacity: 0;
		@media (max-width: 42rem) {
			font-size: theme('fontSize.2xl');
		}
	}
	button {
		opacity: 0;
		border-radius: theme('borderRadius.lg');
		height: 40px;
		padding: 10px 24px;
		justify-content: center;
		align-items: center;
		color: white;
		font-size: theme('fontSize.sm');
		font-weight: 400;
		line-height: 20px;
		&:hover {
			text-decoration: underline;
		}
		&:active {
			text-decoration: underline;
		}
	}

	:global(.welcome-splash .welcome-splash__video) {
		opacity: 0;
	}

	.bg-1,
	.bg-2 {
		display: block;
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		margin: auto;
		width: 90vh;
		height: 90vh;
		background-image: radial-gradient(circle at 50% 50%, rgba(2, 27, 135, .3) 10%, rgba(2, 27, 135, .8) 60%, transparent 70%);
		background-repeat: no-repeat;
		overflow: visible;
		opacity: 0;
		z-index: 999998;
	}
	.bg-2 {
		background-image: radial-gradient(circle at 50% 50%, rgba(2, 27, 135, .3) 10%, rgba(2, 27, 135, .8) 60%, transparent 70%);
	}
</style>
